import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	Layout,
	Row,
	Col,
	Card,
	Table,
	Button,
	message,
	Drawer,
	Popover,
	Popconfirm,
	Dropdown,
	Menu,
	Alert,
} from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	onRowWithRangeSelection,
	renderCurrencyWithZeroByCurrencyId,
	roundOffToDecimal,
	sortById,
	stopDefaultEvents,
} from '../../utils/DataAccessUtils';
import {
	markOrderedPurchaseOrderForSupplier,
	purchaseOrdersRestCrudThunksForSupplier,
	updatePurchaseOrderPartially,
} from '../../thunks/purchase_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ContactRowDisplay } from '../contact_row_display/ContactRowDisplay';
import { AddressDisplay } from '../address_display/AddressDisplay';
import { HorizontalKeyValueDisplay } from '../horizontal_key_value_display/HorizontalKeyValueDisplay';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import PurchaseOrderStatusDisplay from '../purchase_order_status_display/PurchaseOrderStatusDisplay';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';
import {
	PURCHASE_ORDER_STATUSES,
	RECEIVABLE_PO_STATUSES,
} from '../../constants/PurchaseOrderStatuses';
import { purchaseOrderReceiptsRestCrudThunksForSupplier } from '../../thunks/purchase_order_receipt_thunks';
import UpdateSerialAmdAssetNumbersModal from './UpdateSerialAmdAssetNumbersModal';
import { createMultipleEpslAsDeletedForSupplier } from '../../thunks/equipment_per_stock_locations_thunks';
import InfoWithKeyValuePair from '../supplier_assets_detail_page/InfoWithKeyValuePair';
import PRWarrantyAssetDetail from '../purchase_requests_detail_details_page/PRWarrantyAssetDetail';
import { BrandDisplay } from '../brands_overview_index_page/BrandsOverviewIndexPage';
import { PurchaseRequestPartLineItemRowDisplay } from '../purchase_request_part_line_item_row_display/PurchaseRequestPartLineItemRowDisplay';
import { EditOutlined, MoreOutlined } from '@ant-design/icons';
import PartForm from '../part_form/PartForm';
import EquipmentForm from '../equipment_form/EquipmentForm';
import { createMultipleAssetsAsDeletedForSupplier } from '../../thunks/assets_thunks';
import ReplacePartLineItemModal from '../purchase_order_form/ReplacePartLineItemModal';
import ReplaceEquipmentLineItemModal from '../purchase_order_form/ReplaceEquipmentLineItemModal';
import DateTimeHover from '../date_time_component/DateTime';
import {
	getPOLineItemUnitCostValue,
	isGLCodeAllowedBySettings,
	PO_INTEGRATION_STATUS_DISPLAY_NAMES,
} from '../../utils/InventoryUtils';
import ReceivePurchaseOrderLineItemModal from './ReceivePurchaseOrderLineItemModal';
import { removeLineItemFromPO } from '../../thunks/purchase_order_line_items_thunks';
import RemovedStatusDisplay from '../purchase_order_status_display/RemovedStatusDisplay';
import OWEditableNotesComponent from '../common/OWEditableNotesComponent';
import ViaStockLocationButton from './ViaStockLocationButton';
import EquipmentPOLineItemWithViaSLDisplay from './EquipmentPOLineItemWithViaSLDisplay';
import POLiGLCodeDisplay from './POLiGLCodeDisplay';
import EditPOLiGLCodeModal from '../parts_index_page/EditPOLiGLCodeModal';
import { checkAndFormatPhoneNumber } from '../../utils/DataFormatterUtils';
import { sendToIntegration } from '../../thunks/integrator_thunks';

const { Content } = Layout;
const DEFAULT_BACK_LINK_TEXT = 'Back to purchase order';

require('./PurchaseOrdersDetailDetailsPage.less');

const PurchaseOrdersDetailPage: FC<any> = ({
	match,
	getPurchaseOrder,
	purchaseOrder,
	history,
	purchaseOrdersFetching,
	location,
	createPOReceiptBulk,
	markOrdered,
	createEpslsBulkAsDeleted,
	createAssetsBulkAsDeleted,
	removeLineItem,
	partialPOUpdate,
	companyConfig,
	sendToIntegrate,
}): React.ReactElement => {
	const [currentLineItem, setCurrentLineItem] = useState(null);
	const [receiveModalVisible, setReceiveModalVisible] = useState(false);
	const [receivingAllParts, setReceivingAllParts] = useState(false);
	const [receivingAllEquipment, setReceivingAllEquipment] = useState(false);
	const [isReturn, setIsReturn] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [sendForIntegrationLoading, setSendForIntegrationLoading] = useState(false);

	const [equipmentSerialNumberVisible, setEquipmentSerialNumberVisible] = useState(false);
	const [receivingEntityType, setReceivingEntityType] = useState('equipment');
	const [createdEntities, setCreatedEntities] = useState([]);
	const [receiptEntities, setReceiptEntities] = useState<any>({});

	const [statusUpdating, setStatusUpdating] = useState(false);
	const [currentlyEditingPart, setCurrentlyEditingPart] = useState(null);
	const [partEditDrawerVisible, setPartEditDrawerVisible] = useState(false);
	const [currentlyEditingEquipment, setCurrentlyEditingEquipment] = useState(null);
	const [equipmentEditDrawerVisible, setEquipmentEditDrawerVisible] = useState(false);

	const [editingLineItem, setEditingLineItem] = useState(null);
	const [replacePartModalVisible, setReplacePartModalVisible] = useState(false);
	const [replaceEquipmentModalVisible, setReplaceEquipmentModalVisible] = useState(false);

	const [removingLineItem, setRemovingLineItem] = useState(false);
	const [glCodeEditVisible, setGlCodeEditVisible] = useState(false);

	const purchaseOrderId = match.params.id;

	const refreshPurchaseOrder = useCallback(() => {
		if (nullSafeGet('id', purchaseOrder)) {
			message.loading({ content: 'Updating Purchase Order..', key: 'updatingPurchaseOrder' });
		}
		getPurchaseOrder(purchaseOrderId)
			.catch((e) => {
				history.go(0);
			})
			.finally(() => {
				message.destroy('updatingPurchaseOrder');
			});
	}, [purchaseOrder, getPurchaseOrder, purchaseOrderId, history]);

	useEffect(() => {
		if (!nullSafeGet('id', purchaseOrder)) {
			refreshPurchaseOrder();
		}
	}, []);

	const onUserNotesUpdate = useCallback(
		(internalNotes) => partialPOUpdate({ id: purchaseOrderId, internalNotes }),
		[partialPOUpdate, purchaseOrderId]
	);

	const currencyId = useMemo(() => purchaseOrder.currencyId, [purchaseOrder.currencyId]);

	const equipmentLineItems = useMemo(
		() => nullSafeGetOrElse('equipmentSupplierPurchaseOrderLineItems', purchaseOrder, []),
		[purchaseOrder]
	);

	const allEquipmentLinesHasViaStockLocation = useMemo(
		() => equipmentLineItems.every((_) => !!_.viaStockLocationId),
		[equipmentLineItems]
	);

	const someEquipmentLinesHasViaStockLocation = useMemo(
		() => equipmentLineItems.some((_) => !!_.viaStockLocationId),
		[equipmentLineItems]
	);

	const receiveAllDisabledWithoutViaSL = useMemo(
		() => someEquipmentLinesHasViaStockLocation && !allEquipmentLinesHasViaStockLocation,
		[allEquipmentLinesHasViaStockLocation, someEquipmentLinesHasViaStockLocation]
	);

	const receivable = useMemo(
		() => RECEIVABLE_PO_STATUSES.includes(nullSafeGet('status', purchaseOrder)),
		[purchaseOrder]
	);

	const hideSerialNumberUpdate = useCallback(() => setEquipmentSerialNumberVisible(false), []);

	const showReceiveLineItem = useCallback(
		(lineItem, isReturn = false) =>
			(e) => {
				stopDefaultEvents(e);
				setIsReturn(isReturn);
				setCurrentLineItem(lineItem);
				setReceiveModalVisible(true);
			},
		[]
	);

	const hideReceiveLineItem = useCallback(() => {
		setReceiveModalVisible(false);
		setCurrentLineItem(null);
	}, []);

	const onPOReceiptSuccess = useCallback(
		(receipt) => {
			message.success(`${receipt.receivedQuantity > 0 ? 'Received' : 'Returned'} successfully!`);
			refreshPurchaseOrder();
			hideReceiveLineItem();
		},
		[hideReceiveLineItem, refreshPurchaseOrder]
	);

	const onEquipmentCreateSuccess = useCallback(
		(receipt, deletedEntities) => {
			hideReceiveLineItem();
			setReceiptEntities([receipt]);
			setCreatedEntities(deletedEntities);
			setReceivingEntityType(
				nullSafeGet('0.equipmentTypeId', deletedEntities) ? 'equipment' : 'asset'
			);
			setEquipmentSerialNumberVisible(true);
		},
		[hideReceiveLineItem]
	);

	const onUpdateSerialNumberSuccess = useCallback(() => {
		setCreatedEntities([]);
		setEquipmentSerialNumberVisible(false);
		refreshPurchaseOrder();
	}, [refreshPurchaseOrder]);

	const renderCurrencyWithWarrantyIfAvailable = useCallback(
		(record, cost = 0) => (
			<div className="flex flex-row items-center">
				<div>{renderCurrencyWithZeroByCurrencyId(currencyId)(cost)}</div>
				{record.warrantyAssetId ? (
					<InfoWithKeyValuePair
						details={{
							'Warranty Asset': (
								<PRWarrantyAssetDetail
									id={record.warrantyAssetId}
									asset={record.warrantyAsset}
									backLinkText={'Back To Purchase Order Detail'}
									timeZoneId={nullSafeGet('supplierPurchaseOrder.location.timeZoneId', record)}
								/>
							),
						}}
					/>
				) : null}
			</div>
		),
		[currencyId]
	);

	const editPart = useCallback(
		(lineItem) => (e) => {
			stopDefaultEvents(e);
			setCurrentlyEditingPart(lineItem.part);
			setEditingLineItem(lineItem);
			setPartEditDrawerVisible(true);
		},
		[]
	);

	const replacePartLineItem = () => setReplacePartModalVisible(true);
	const _hideReplacePartModal = () => setReplacePartModalVisible(false);

	const replaceEquipmentLineItem = () => setReplaceEquipmentModalVisible(true);
	const _hideReplaceEquipmentModal = () => setReplaceEquipmentModalVisible(false);

	const _showGLCodeEditModal = (lineItem) => (e) => {
		stopDefaultEvents(e);
		setCurrentLineItem(lineItem);
		setGlCodeEditVisible(true);
	};
	const _hideGLCodeEditModal = () => {
		setCurrentLineItem(null);
		setGlCodeEditVisible(false);
	};
	const onGLCodeSuccess = useCallback(() => {
		refreshPurchaseOrder();
		_hideGLCodeEditModal();
	}, [refreshPurchaseOrder]);

	const onPartEditClose = useCallback(() => {
		setPartEditDrawerVisible(false);
		setCurrentlyEditingPart(null);
		setEditingLineItem(null);
	}, []);

	const onPartEditSuccess = useCallback(() => {
		onPartEditClose();
		refreshPurchaseOrder();
	}, [onPartEditClose, refreshPurchaseOrder]);

	const onReplacePartLineItemSuccess = useCallback(() => {
		_hideReplacePartModal();
		onPartEditClose();
		refreshPurchaseOrder();
	}, [onPartEditClose, refreshPurchaseOrder]);

	const onEquipmentEditClose = useCallback(() => {
		setEquipmentEditDrawerVisible(false);
		setCurrentlyEditingEquipment(null);
		setEditingLineItem(null);
	}, []);

	const onReplaceEquipmentLineItemSuccess = useCallback(() => {
		_hideReplaceEquipmentModal();
		onEquipmentEditClose();
		refreshPurchaseOrder();
	}, [onEquipmentEditClose, refreshPurchaseOrder]);

	const receivedItemMenu = useCallback(
		(record) => (
			<Menu>
				<Menu.Item onClick={showReceiveLineItem(record)}>Receive More</Menu.Item>
			</Menu>
		),
		[showReceiveLineItem]
	);

	const renderReceiveMore = useCallback(
		(_, record) =>
			record.status === PURCHASE_ORDER_STATUSES.received ? (
				<Dropdown trigger={['click']} overlay={receivedItemMenu(record)}>
					<div className="cursor-pointer" onClick={stopDefaultEvents}>
						<MoreOutlined />
					</div>
				</Dropdown>
			) : null,
		[receivedItemMenu]
	);

	const allPartLineItems = useMemo(
		() => [
			...sortById(nullSafeGetOrElse('partSupplierPurchaseOrderLineItems', purchaseOrder, [])),
			...sortById(
				nullSafeGetOrElse('deletedPartSupplierPurchaseOrderLineItems', purchaseOrder, [])
			),
		],
		[purchaseOrder]
	);

	const allEquipmentLineItems = useMemo(
		() => [
			...sortById(nullSafeGetOrElse('equipmentSupplierPurchaseOrderLineItems', purchaseOrder, [])),
			...sortById(
				nullSafeGetOrElse('deletedEquipmentSupplierPurchaseOrderLineItems', purchaseOrder, [])
			),
		],
		[purchaseOrder]
	);

	const partFilters = useMemo(
		() =>
			allPartLineItems
				.map((pli) => ({
					text: nullSafeGetOrElse('part.partNumber', pli, ''),
					value: nullSafeGetOrElse('part.partNumber', pli, ''),
				}))
				.filter((_) => !!_.text),
		[allPartLineItems]
	);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	const partsColumns = useMemo(
		() => [
			{
				title: 'Part Number',
				dataIndex: ['part'],
				filters: partFilters,
				filterSearch: true,
				onFilter: (value: string, record) =>
					nullSafeGetOrElse('part.partNumber', record, '') === value,
				render: (part, record) => (
					<div className="flex flex-row items-center">
						<div>{nullSafeGet('partNumber', part) || '--'}</div>
						{record.isDeleted ? null : (
							<Button type="link" icon={<EditOutlined />} onClick={editPart(record)} />
						)}
					</div>
				),
			},
			{
				title: 'Part Detail',
				render: (_, record) => (
					<PurchaseRequestPartLineItemRowDisplay purchaseRequestPartLineItem={record} />
				),
			},
			...(glCodeEnabled
				? [
						{
							title: 'GL Code',
							dataIndex: 'glCode',
							render: (_, record) => (
								<POLiGLCodeDisplay
									purchaseOrderLineItem={record}
									onEdit={_showGLCodeEditModal(record)}
								/>
							),
						},
				  ]
				: []),
			{
				title: 'Quantity',
				dataIndex: 'quantity',
				render: (_, record) => (
					<div style={{ whiteSpace: 'nowrap' }}>
						{nullSafeGetOrElse('partQuantity', record, '--')}{' '}
						{nullSafeGetOrElse('partQuantity', record, 0) > 1
							? nullSafeGet('partUom.displayNamePlural', record)
							: nullSafeGet('partUom.displayNameSingular', record)}
					</div>
				),
			},
			{
				title: 'Received',
				render: (_, record) => nullSafeGetOrElse('partReceivedQuantity', record, 0),
			},
			{
				title: 'Unit Cost',
				render: (_, record) => {
					const unitCost = getPOLineItemUnitCostValue(record, 'part')['unitCost'];
					return renderCurrencyWithWarrantyIfAvailable(record, unitCost || 0);
				},
			},
			{
				title: 'Total Cost',
				dataIndex: 'partCost',
				render: (_, record) => {
					const totalCost = getPOLineItemUnitCostValue(record, 'part')['totalCost'];
					return renderCurrencyWithWarrantyIfAvailable(record, totalCost || 0);
				},
			},
			{
				title: 'Status',
				render: (_, record) =>
					record.isDeleted ? (
						<RemovedStatusDisplay />
					) : record.status ? (
						<PurchaseOrderStatusDisplay status={record.status} />
					) : (
						'NA'
					),
			},
			...(receivable
				? [
						{
							title: '',
							render: (_, record) =>
								record.isDeleted ? null : (
									<div className="grid gap-2">
										<Button
											disabled={record.status === PURCHASE_ORDER_STATUSES.received}
											onClick={showReceiveLineItem(record)}
										>
											Receive
										</Button>
										<Button
											disabled={nullSafeGetOrElse('partReceivedQuantity', record, 0) < 1}
											onClick={showReceiveLineItem(record, true)}
										>
											Return
										</Button>
									</div>
								),
						},
				  ]
				: []),
			{
				title: '',
				render: renderReceiveMore,
			},
		],
		[
			editPart,
			glCodeEnabled,
			partFilters,
			receivable,
			renderCurrencyWithWarrantyIfAvailable,
			renderReceiveMore,
			showReceiveLineItem,
		]
	);

	const editEquipment = useCallback(
		(lineItem) => (e) => {
			stopDefaultEvents(e);
			setCurrentlyEditingEquipment(lineItem.equipmentType);
			setEditingLineItem(lineItem);
			setEquipmentEditDrawerVisible(true);
		},
		[]
	);

	const onEquipmentEditSuccess = useCallback(() => {
		onEquipmentEditClose();
		refreshPurchaseOrder();
	}, [onEquipmentEditClose, refreshPurchaseOrder]);

	const removePartOrEquipmentLineItem = useCallback(
		(type) => () => {
			setRemovingLineItem(true);
			removeLineItem(editingLineItem.id)
				.then(() => {
					message.success('Removed successfully!');
					type === 'part' ? onPartEditSuccess() : onEquipmentEditSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setRemovingLineItem(false));
		},
		[editingLineItem, onEquipmentEditSuccess, onPartEditSuccess, removeLineItem]
	);

	const equTypeFilters = useMemo(
		() =>
			allEquipmentLineItems
				.map((eli) => ({
					text: nullSafeGetOrElse('equipmentType.equipmentTypeNumber', eli, ''),
					value: nullSafeGetOrElse('equipmentType.equipmentTypeNumber', eli, ''),
				}))
				.filter((_) => !!_.text),
		[allEquipmentLineItems]
	);

	const equipmentColumns: any[] = useMemo(
		() => [
			{
				title: 'Equipment Type #',
				dataIndex: ['equipmentType'],
				filters: equTypeFilters,
				filterMode: 'tree',
				filterSearch: true,
				onFilter: (value: string, record) =>
					nullSafeGetOrElse('equipmentType.equipmentTypeNumber', record, '') === value,
				render: (equipmentType, record) => (
					<div className="flex flex-row items-center">
						<div>{nullSafeGet('equipmentTypeNumber', equipmentType) || '--'}</div>
						{record.isDeleted ? null : (
							<Button type="link" icon={<EditOutlined />} onClick={editEquipment(record)} />
						)}
					</div>
				),
			},
			{
				title: 'Equipment Detail',
				render: (_, record) => <EquipmentPOLineItemWithViaSLDisplay lineItem={record} />,
			},
			...(glCodeEnabled
				? [
						{
							title: 'GL Code',
							dataIndex: 'glCode',
							render: (_, record) => (
								<POLiGLCodeDisplay
									purchaseOrderLineItem={record}
									onEdit={_showGLCodeEditModal(record)}
								/>
							),
						},
				  ]
				: []),
			{
				title: 'Quantity',
				dataIndex: 'equipmentQuantity',
			},
			{
				title: 'Received',
				render: (_, record) => nullSafeGetOrElse('equipmentReceivedQuantity', record, 0),
			},
			{
				title: 'Unit Cost',
				render: (_, record) => {
					const unitCost = getPOLineItemUnitCostValue(record, 'equipment')['unitCost'];
					return renderCurrencyWithWarrantyIfAvailable(record, unitCost || 0);
				},
			},
			{
				title: 'Total Cost',
				render: (_, record) => {
					const totalCost = getPOLineItemUnitCostValue(record, 'equipment')['totalCost'];
					return renderCurrencyWithWarrantyIfAvailable(record, totalCost || 0);
				},
			},
			{
				title: 'Status',
				render: (_, record) =>
					record.isDeleted ? (
						<RemovedStatusDisplay />
					) : record.status ? (
						<PurchaseOrderStatusDisplay status={record.status} />
					) : (
						'NA'
					),
			},
			...(receivable
				? [
						{
							title: '',
							render: (_, record) =>
								record.isDeleted ? null : (
									<>
										<Button
											disabled={record.status === PURCHASE_ORDER_STATUSES.received}
											onClick={showReceiveLineItem(record)}
										>
											Receive
										</Button>
										<Button
											disabled={nullSafeGetOrElse('equipmentReceivedQuantity', record, 0) < 1}
											className="ml-2"
											onClick={showReceiveLineItem(record, true)}
										>
											Return
										</Button>
									</>
								),
						},
				  ]
				: []),
			{
				title: '',
				render: renderReceiveMore,
			},
		],
		[
			editEquipment,
			equTypeFilters,
			glCodeEnabled,
			receivable,
			renderCurrencyWithWarrantyIfAvailable,
			renderReceiveMore,
			showReceiveLineItem,
		]
	);

	const shipToLocation = useMemo(() => purchaseOrder.locationId, [purchaseOrder.locationId]);

	const receiveAllEquipmentDisabledWithoutAssetType = useMemo(() => {
		const eqLineItems = nullSafeGetOrElse(
			'equipmentSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		);
		return shipToLocation && nullSafeGet('0', eqLineItems)
			? !!eqLineItems.find((_) => !nullSafeGet('equipmentType.assetTypeId', _))
			: false;
	}, [purchaseOrder, shipToLocation]);

	const shipToName = useMemo(
		() =>
			shipToLocation
				? nullSafeGet(`location.name`, purchaseOrder)
				: nullSafeGet('supplierFacility.displayName', purchaseOrder),
		[purchaseOrder, shipToLocation]
	);

	const shipToPhoneNumber = useMemo(
		() =>
			nullSafeGet('location.phoneNumber', purchaseOrder) ||
			nullSafeGet('stockLocation.location.phoneNumber', purchaseOrder),
		[purchaseOrder]
	);

	const purchaseOrderDetails = useMemo(
		() => ({
			'Ordered by': (
				<ContactRowDisplay
					contact={purchaseOrder.createdByContact}
					userType={ROLE_TYPES.SUPPLIER}
				/>
			),
			'Ship to': (
				<HyperLink
					text={nullSafeGet(`${shipToLocation ? 'location' : 'stockLocation'}.name`, purchaseOrder)}
					entityType={shipToLocation ? ENTITY_TYPE.LOCATION : ENTITY_TYPE.STOCK_LOCATION}
					entityId={nullSafeGet(
						`${shipToLocation ? 'locationId' : 'stockLocationId'}`,
						purchaseOrder
					)}
					backLinkText={DEFAULT_BACK_LINK_TEXT}
				/>
			),
			...(nullSafeGet('brand', purchaseOrder) && {
				Brand: <BrandDisplay brand={purchaseOrder.brand} />,
			}),
			'Shipping address': (
				<div className="flex flex-col">
					<div>{shipToName}</div>
					<AddressDisplay
						address={
							nullSafeGet('location.buyerFacility.primaryAddress', purchaseOrder) ||
							nullSafeGet('stockLocation.address', purchaseOrder) ||
							nullSafeGet('stockLocation.location.buyerFacility.primaryAddress', purchaseOrder)
						}
					/>
					{shipToPhoneNumber ? `${checkAndFormatPhoneNumber(shipToPhoneNumber)}` : null}
				</div>
			),
			Vendor: (
				<HyperLink
					text={nullSafeGet('partEquipmentVendor.name', purchaseOrder)}
					entityType={ENTITY_TYPE.VENDOR}
					entityId={nullSafeGet('partEquipmentVendorId', purchaseOrder)}
					backLinkText={DEFAULT_BACK_LINK_TEXT}
				/>
			),
			'Created at': <DateTimeHover timestamp={nullSafeGet('createdAt', purchaseOrder)} />,
			'Total cost': renderCurrencyWithZeroByCurrencyId(currencyId)(purchaseOrder.totalCost),
			'Receipts Total': renderCurrencyWithZeroByCurrencyId(currencyId)(purchaseOrder.actualCost),
			...(purchaseOrder.integratorName ? { 'Integrator Name': purchaseOrder.integratorName } : {}),
			...(purchaseOrder.integratorStatus
				? {
						'Integration Status':
							PO_INTEGRATION_STATUS_DISPLAY_NAMES[purchaseOrder.integratorStatus] ||
							purchaseOrder.integratorStatus,
				  }
				: {}),
		}),
		[purchaseOrder, shipToLocation, shipToName, shipToPhoneNumber, currencyId]
	);

	const otherChargesDetails = useMemo(
		() => ({
			'Freight charges': renderCurrencyWithZeroByCurrencyId(currencyId)(
				purchaseOrder.freightTotalBeforeTax
			),
			'Misc charges': renderCurrencyWithZeroByCurrencyId(currencyId)(
				purchaseOrder.miscTotalBeforeTax
			),
			Tax: renderCurrencyWithZeroByCurrencyId(currencyId)(purchaseOrder.tax),
		}),
		[
			currencyId,
			purchaseOrder.freightTotalBeforeTax,
			purchaseOrder.miscTotalBeforeTax,
			purchaseOrder.tax,
		]
	);

	const openEquipmentDetail = useCallback(
		(record) => {
			const path = `/supplier/equipments/detail/${record.equipmentTypeId}`;
			history.push(getLinkWIthBackLinkParams(location, DEFAULT_BACK_LINK_TEXT, path));
		},
		[history, location]
	);

	const equipmentOnRow = (record) => ({
		onClick: () => openEquipmentDetail(record),
	});

	const openPartDetail = useCallback(
		(record) => {
			const path = `/supplier/parts/detail/${record.partId}`;
			history.push(getLinkWIthBackLinkParams(location, DEFAULT_BACK_LINK_TEXT, path));
		},
		[history, location]
	);

	const partsOnRow = (record) => ({
		onClick: () => openPartDetail(record),
	});

	const onReceiveAllParts = useCallback(() => {
		const validEntities = nullSafeGetOrElse(
			'partSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		).filter((li) => li.partQuantity - nullSafeGetOrElse('partReceivedQuantity', li, 0) > 0);
		const entities = validEntities.map((_) => ({
			purchaseOrderLineItemId: _.id,
			purchaseOrderId: purchaseOrder.id,
			receiptNumber: '',
			pricePerQuantity: roundOffToDecimal(
				nullSafeGetOrElse('partCost', _, 0) / nullSafeGetOrElse('partQuantity', _, 1)
			),
			partId: _.partId,
			receivedQuantity: _.partQuantity - nullSafeGetOrElse('partReceivedQuantity', _, 0),
		}));
		setReceivingAllParts(true);
		createPOReceiptBulk(entities)
			.then(() => {
				message.success('Parts received successfully!');
				refreshPurchaseOrder();
			})
			.catch(() => message.error('Unable to receive parts!'))
			.finally(() => setReceivingAllParts(false));
	}, [createPOReceiptBulk, refreshPurchaseOrder, purchaseOrder]);

	const createDeletedAssetsBulk = useCallback(() => {
		const allAssetEntities = nullSafeGetOrElse(
			'equipmentSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		).flatMap((lineItem) => {
			const quantity =
				lineItem.equipmentQuantity - nullSafeGetOrElse('equipmentReceivedQuantity', lineItem, 0);
			const entities = Array.from(Array(quantity).keys()).map((_) => ({
				locationId: nullSafeGet('supplierPurchaseOrder.locationId', lineItem),
				name: nullSafeGetOrElse('equipmentType.modelName', lineItem, ''),
				isActive: true,
				images: [],
				...(nullSafeGet('equipmentType.assetTypeId', lineItem) && {
					assetTypeId: nullSafeGet('equipmentType.assetTypeId', lineItem),
				}),
				assetTypeDetails: [],
				isLeased: false,
				buyerCompanyId: nullSafeGet('supplierPurchaseOrder.location.buyerCompanyId', lineItem),
			}));
			return entities;
		});

		return createAssetsBulkAsDeleted(allAssetEntities);
	}, [createAssetsBulkAsDeleted, purchaseOrder]);

	const createDeletedEpslsBulk = useCallback(() => {
		const allEpslEntities = nullSafeGetOrElse(
			'equipmentSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		).flatMap((lineItem) => {
			const quantity =
				lineItem.equipmentQuantity - nullSafeGetOrElse('equipmentReceivedQuantity', lineItem, 0);
			const entities = Array.from(Array(quantity).keys()).map((_) => ({
				stockLocationId:
					nullSafeGet('viaStockLocationId', lineItem) ||
					nullSafeGet('supplierPurchaseOrder.stockLocationId', lineItem),
				equipmentTypeId: lineItem.equipmentTypeId,
				name: nullSafeGetOrElse('equipmentType.modelName', lineItem, ''),
				isRental: false,
				vendorId: nullSafeGet('supplierPurchaseOrder.partEquipmentVendorId', lineItem),
				images: [],
				supplierFacilityId: lineItem.supplierFacilityId,
				supplierCompanyId: lineItem.supplierCompanyId,
			}));
			return entities;
		});

		return createEpslsBulkAsDeleted(allEpslEntities);
	}, [createEpslsBulkAsDeleted, purchaseOrder]);

	const onReceiveAllEquipment = useCallback(() => {
		setReceivingAllEquipment(true);

		const promiseToCall = allEquipmentLinesHasViaStockLocation
			? createDeletedEpslsBulk
			: createDeletedAssetsBulk;

		promiseToCall()
			.then((res) => {
				setCreatedEntities(res);

				const validEntities = nullSafeGetOrElse(
					'equipmentSupplierPurchaseOrderLineItems',
					purchaseOrder,
					[]
				).filter(
					(li) => li.equipmentQuantity - nullSafeGetOrElse('equipmentReceivedQuantity', li, 0) > 0
				);

				const receipts = validEntities.map((_) => ({
					purchaseOrderLineItemId: _.id,
					purchaseOrderId: _.supplierPurchaseOrderId,
					receiptNumber: '',
					pricePerQuantity: roundOffToDecimal(
						nullSafeGetOrElse('equipmentCost', _, 0) / nullSafeGetOrElse('equipmentQuantity', _, 1)
					),
					equipmentTypeId: _.equipmentTypeId,
					...(nullSafeGet('equipmentType.assetTypeId', _) && {
						assetTypeId: nullSafeGet('equipmentType.assetTypeId', _),
					}),
					receivedQuantity:
						_.equipmentQuantity - nullSafeGetOrElse('equipmentReceivedQuantity', _, 0),
				}));

				setReceiptEntities(receipts);
				setReceivingEntityType(allEquipmentLinesHasViaStockLocation ? 'equipment' : 'asset');
				setEquipmentSerialNumberVisible(true);
			})
			.catch((err) => message.error(err))
			.finally(() => setReceivingAllEquipment(false));
	}, [
		allEquipmentLinesHasViaStockLocation,
		createDeletedAssetsBulk,
		createDeletedEpslsBulk,
		purchaseOrder,
	]);

	const onOrdered = useCallback(() => {
		setStatusUpdating(true);
		markOrdered(purchaseOrder)
			.then(() => refreshPurchaseOrder())
			.catch(() => message.error('Unable to update Purchase Order!'))
			.finally(() => setStatusUpdating(false));
	}, [markOrdered, purchaseOrder, refreshPurchaseOrder]);

	const fetching = refreshing || (purchaseOrdersFetching && !nullSafeGet('id', purchaseOrder));

	const canReplaceOrRemove = useMemo(
		() =>
			nullSafeGetOrElse(`partReceivedQuantity`, editingLineItem, 0) <= 0 &&
			nullSafeGetOrElse(`equipmentReceivedQuantity`, editingLineItem, 0) <= 0,
		[editingLineItem]
	);

	const integratorName = useMemo(
		() => nullSafeGet('integratorName', purchaseOrder),
		[purchaseOrder]
	);

	const handleSendToIntegration = useCallback(() => {
		setSendForIntegrationLoading(true);
		sendToIntegrate(purchaseOrder.id)
			.then(() => {
				refreshPurchaseOrder();
			})
			.catch((err) => message.error(err || 'Error sending for integration.'))
			.finally(() => {
				setSendForIntegrationLoading(false);
			});
	}, [purchaseOrder.id, refreshPurchaseOrder, sendToIntegrate]);

	const integrationMessageContent = useMemo(() => {
		const integratorStatus = nullSafeGet('integratorStatus', purchaseOrder);
		if (integratorStatus === 'failure') {
			return (
				<div className="flex flex-row items-center justify-between">
					<div>{`Pushing this PO to ${integratorName} has failed. Please check Notes for updates.`}</div>
					<Button loading={sendForIntegrationLoading} onClick={handleSendToIntegration}>
						{`Send to ${integratorName}`}
					</Button>
				</div>
			);
		} else if (integratorStatus === 'in_progress') {
			return `PUshing this PO to ${integratorName} is in progress.`;
		} else {
			return `This PO has been pushed to ${integratorName}. Please check Notes for updates.`;
		}
	}, [handleSendToIntegration, integratorName, purchaseOrder, sendForIntegrationLoading]);

	const integrationMessage = useMemo(() => {
		const integratorStatus = nullSafeGet('integratorStatus', purchaseOrder);

		return integratorName ? (
			<Alert
				message={integrationMessageContent}
				type={integratorStatus === 'failure' ? 'error' : 'success'}
			/>
		) : null;
	}, [integrationMessageContent, integratorName, purchaseOrder]);

	return fetching ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="purchaseOrdersDetailDetailsPage">
			<LogOnMountWithStandardEventProperties eventType="visited purchase order detail page" />
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={18} className="rowSpacing">
					<Card>
						{purchaseOrder.status === 'new' ? (
							<div>
								<div className="mb-4">{integrationMessage}</div>
								<h5>Has this Purchase Order been sent to vendor already?</h5>
								<div style={{ marginTop: 16, marginBottom: 40 }}>
									<Popover
										trigger="hover"
										content={
											integratorName
												? `Sent for integration to ${integratorName}; you cannot mark this as 'Ordered'.`
												: ''
										}
									>
										<Button
											type="primary"
											key="approve"
											onClick={onOrdered}
											loading={statusUpdating}
											disabled={integratorName}
										>
											Yes, it was ordered
										</Button>
									</Popover>
								</div>
							</div>
						) : null}
						<HorizontalKeyValueDisplay
							rowStyle={{ minWidth: '25%' }}
							keyValueStore={purchaseOrderDetails}
						/>
					</Card>
				</Col>
				<Col span={6} className="rowSpacing">
					<div style={{ height: '100%' }}>
						<OWEditableNotesComponent
							style={{ height: '100%' }}
							loading={fetching}
							id="poNotes"
							tooltipMessage=""
							title="User Notes"
							allowEdit={true}
							notes={nullSafeGetOrElse('internalNotes', purchaseOrder, '')}
							onSaveNotes={onUserNotesUpdate}
						/>
					</div>
				</Col>
			</Row>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={18} className="rowSpacing">
					<Card title={'Description'} style={{ height: '100%' }}>
						<div
							className="richTextWrapper"
							dangerouslySetInnerHTML={{
								__html: nullSafeGetOrElse('description', purchaseOrder, '--'),
							}}
						/>
					</Card>
				</Col>
				<Col span={6} className="rowSpacing">
					<Card title={'Payment Terms'}>
						<div>{nullSafeGetOrElse('paymentTerms', purchaseOrder, '--')}</div>
					</Card>

					<Card title={'Shipping Terms'}>
						<div>{nullSafeGetOrElse('shippingTerms', purchaseOrder, '--')}</div>
					</Card>
				</Col>
			</Row>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				{allPartLineItems.length > 0 ? (
					<Col span={24} className="rowSpacing">
						<Card
							title={`Parts`}
							extra={
								receivable &&
								purchaseOrder.partSupplierPurchaseOrderLineItems.some(
									(_) => _.status !== PURCHASE_ORDER_STATUSES.received
								) ? (
									<Button type="primary" onClick={onReceiveAllParts} loading={receivingAllParts}>
										Receive All
									</Button>
								) : null
							}
						>
							<Table
								columns={partsColumns}
								rowKey={(partLineItem) => partLineItem['id']}
								dataSource={allPartLineItems}
								showHeader={true}
								loading={false}
								onRow={onRowWithRangeSelection(partsOnRow)}
							/>
						</Card>
					</Col>
				) : null}
				{allEquipmentLineItems.length > 0 ? (
					<Col span={24} className="rowSpacing">
						<Card
							title={`Equipment`}
							extra={
								receivable &&
								purchaseOrder.equipmentSupplierPurchaseOrderLineItems.some(
									(_) => _.status !== PURCHASE_ORDER_STATUSES.received
								) ? (
									<div className="flex flex-row gap-2">
										<ViaStockLocationButton purchaseOrder={purchaseOrder} />
										<Popover
											content={
												receiveAllEquipmentDisabledWithoutAssetType
													? 'Please associate asset type with every equipment type in this purchase order!'
													: receiveAllDisabledWithoutViaSL
													? `Receive all cannot be done when few line items doesn't have forwarding Stock location`
													: null
											}
											trigger="hover"
										>
											<Button
												type="primary"
												onClick={onReceiveAllEquipment}
												loading={receivingAllEquipment}
												disabled={
													receiveAllEquipmentDisabledWithoutAssetType ||
													receiveAllDisabledWithoutViaSL
												}
											>
												Receive All
											</Button>
										</Popover>
									</div>
								) : null
							}
						>
							<Table
								columns={equipmentColumns}
								rowKey={(equipment) => equipment['id']}
								dataSource={allEquipmentLineItems}
								showHeader={true}
								loading={false}
								onRow={onRowWithRangeSelection(equipmentOnRow)}
							/>
						</Card>
					</Col>
				) : null}
				<Col span={24} className="rowSpacing">
					<Card title={`Other Charges`}>
						<HorizontalKeyValueDisplay
							rowStyle={{ minWidth: '25%' }}
							keyValueStore={otherChargesDetails}
						/>
					</Card>
				</Col>
			</Row>
			{receiveModalVisible && (
				<ReceivePurchaseOrderLineItemModal
					lineItem={currentLineItem}
					isReturn={isReturn}
					onCancel={hideReceiveLineItem}
					onPOReceiptSuccess={onPOReceiptSuccess}
					onEquipmentCreateSuccess={onEquipmentCreateSuccess}
				/>
			)}
			{equipmentSerialNumberVisible && (
				<UpdateSerialAmdAssetNumbersModal
					receiptEntities={receiptEntities}
					createdEntities={createdEntities}
					entityType={receivingEntityType}
					onSuccess={onUpdateSerialNumberSuccess}
					onCancel={hideSerialNumberUpdate}
				/>
			)}
			{partEditDrawerVisible ? (
				<Drawer
					width={640}
					placement="right"
					closable={true}
					onClose={onPartEditClose}
					visible={true}
					extra={
						<div className="flex flex-row items-center">
							<Popover
								content={!canReplaceOrRemove ? 'Received items cannot be removed' : ''}
								trigger="hover"
							>
								<Popconfirm
									onConfirm={removePartOrEquipmentLineItem('part')}
									placement="topLeft"
									title="Are you sure you want to remove?"
								>
									<Button type="ghost" loading={removingLineItem} disabled={!canReplaceOrRemove}>
										Remove Part
									</Button>
								</Popconfirm>
							</Popover>
							<div className="ml-2">
								<Popover
									content={!canReplaceOrRemove ? 'Received items cannot be replaced' : ''}
									trigger="hover"
								>
									<Button type="link" disabled={!canReplaceOrRemove} onClick={replacePartLineItem}>
										Replace Part
									</Button>
								</Popover>
							</div>
						</div>
					}
				>
					<PartForm
						onCancel={onPartEditClose}
						formData={currentlyEditingPart}
						onSuccess={onPartEditSuccess}
					/>
				</Drawer>
			) : null}
			{equipmentEditDrawerVisible ? (
				<Drawer
					width={640}
					placement="right"
					closable={true}
					onClose={onEquipmentEditClose}
					visible={true}
					extra={
						<div className="flex flex-row items-center">
							<Popover
								content={!canReplaceOrRemove ? 'Received items cannot be removed' : ''}
								trigger="hover"
							>
								<Popconfirm
									onConfirm={removePartOrEquipmentLineItem('equipment')}
									placement="topLeft"
									title="Are you sure you want to remove?"
								>
									<Button type="ghost" loading={removingLineItem} disabled={!canReplaceOrRemove}>
										Remove Equipment
									</Button>
								</Popconfirm>
							</Popover>
							<div className="ml-2">
								<Popover
									content={!canReplaceOrRemove ? 'Received items cannot be replaced' : ''}
									trigger="hover"
								>
									<Button
										type="link"
										disabled={!canReplaceOrRemove}
										onClick={replaceEquipmentLineItem}
									>
										Replace Equipment
									</Button>
								</Popover>
							</div>
						</div>
					}
				>
					<EquipmentForm
						onCancel={onEquipmentEditClose}
						formData={currentlyEditingEquipment}
						onSuccess={onEquipmentEditSuccess}
					/>
				</Drawer>
			) : null}
			{replacePartModalVisible ? (
				<ReplacePartLineItemModal
					lineItem={editingLineItem}
					vendorId={nullSafeGet('partEquipmentVendorId', purchaseOrder)}
					onCancel={_hideReplacePartModal}
					onSuccess={onReplacePartLineItemSuccess}
				/>
			) : null}
			{replaceEquipmentModalVisible ? (
				<ReplaceEquipmentLineItemModal
					lineItem={editingLineItem}
					vendorId={nullSafeGet('partEquipmentVendorId', purchaseOrder)}
					onCancel={_hideReplaceEquipmentModal}
					onSuccess={onReplaceEquipmentLineItemSuccess}
				/>
			) : null}
			{glCodeEditVisible ? (
				<EditPOLiGLCodeModal
					purchaseOrderLineItem={currentLineItem}
					onSuccess={onGLCodeSuccess}
					onCancel={_hideGLCodeEditModal}
				/>
			) : null}
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	purchaseOrdersFetching: state.purchase_orders.fetching,
	purchaseOrder: getEntityById(ownProps.match.params.id, state.purchase_orders),
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	getPurchaseOrder: (id) => dispatch(purchaseOrdersRestCrudThunksForSupplier.readOne(id)),
	createPOReceiptBulk: (entities) =>
		dispatch(purchaseOrderReceiptsRestCrudThunksForSupplier.createBulk(entities)),
	markOrdered: (entity) => dispatch(markOrderedPurchaseOrderForSupplier(entity)),
	createEpslsBulkAsDeleted: (entities) =>
		dispatch(createMultipleEpslAsDeletedForSupplier(entities)),
	createAssetsBulkAsDeleted: (entities) =>
		dispatch(createMultipleAssetsAsDeletedForSupplier(entities)),
	removeLineItem: (id) => dispatch(removeLineItemFromPO(id)),
	partialPOUpdate: (data) => dispatch(updatePurchaseOrderPartially(data)),
	sendToIntegrate: (id) => dispatch(sendToIntegration(id)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PurchaseOrdersDetailPage)
);
